import type Phaser from 'phaser';
import { TILE_SIZE } from '../layout';

/** How far a hill's shadow falls past its foot (right and down), in pixels. */
export const HILL_SHADOW = 3;

/** Light comes from the top left and from above (unit vector toward the sun). */
const SUN = (() => {
  const [x, y, z] = [-0.55, -0.75, 0.55];
  const n = Math.hypot(x, y, z);
  return [x / n, y / n, z / n] as const;
})();

type Rgb = readonly [number, number, number];
/**
 * Shade multiplies the grass under it (so it stays a saturated green, never a gray haze), in a
 * cool blue-green; light is added to it, in a warm yellow-green.
 */
const SHADE: Rgb = [196, 214, 206];
const DEEP: Rgb = [146, 170, 168];
const FOOT: Rgb = [88, 112, 104];
const CAST: Rgb = [176, 196, 186];
const LIT: Rgb = [26, 30, 4];
const BRIGHT: Rgb = [52, 56, 10];
const LIP: Rgb = [84, 84, 26];

export interface HillLayers {
  /** Multiplied over the lawn: the shaded slopes, the dark foot line and the cast shadow. */
  shade: string;
  /** Added over the lawn: the sunlit slopes and the bright lip of the summit. */
  light: string;
}

/**
 * The two texture keys for a w x h tile hill (painted the first time they are asked for), laid
 * over the lawn's own grass so a hill is still lawn to mow. The mound rises from a rounded foot to
 * a flat summit on the middle tile, its slopes lit by how they face the top-left sun in a few crisp
 * bands (cel shading, as pixel art lights a form). A bright lip rims the summit on the sunny side,
 * a dark line traces the foot on the shaded side and the hill casts a short shadow down and right.
 * Each layer is (w x 16 + HILL_SHADOW) px square-ish, anchored at the hill's top-left corner.
 */
export function hillTextures(scene: Phaser.Scene, w: number, h: number): HillLayers {
  const keys = { shade: `hill-${w}x${h}-shade`, light: `hill-${w}x${h}-light` };
  if (scene.textures.exists(keys.shade)) return keys;
  const W = w * TILE_SIZE;
  const H = h * TILE_SIZE;
  const shadeTex = scene.textures.createCanvas(keys.shade, W + HILL_SHADOW, H + HILL_SHADOW);
  const lightTex = scene.textures.createCanvas(keys.light, W + HILL_SHADOW, H + HILL_SHADOW);
  if (!shadeTex || !lightTex) throw new Error('Could not create hill textures');
  const rx = W / 2;
  const ry = H / 2;
  // Distance from the middle: 0 at the summit's center, 1 at the foot (a rounded square).
  const reach = (x: number, y: number) => {
    const u = Math.abs((x - rx) / rx);
    const v = Math.abs((y - ry) / ry);
    return Math.pow(Math.pow(u, 2.6) + Math.pow(v, 2.6), 1 / 2.6);
  };
  const flat = 0.3;
  const rise = Math.min(W, H) * 0.24;
  const height = (x: number, y: number) => {
    const r = reach(x, y);
    if (r >= 1) return 0;
    if (r <= flat) return rise;
    return rise * 0.5 * (1 + Math.cos((Math.PI * (r - flat)) / (1 - flat)));
  };
  const inside = (x: number, y: number) => reach(x + 0.5, y + 0.5) < 1;
  const onTop = (x: number, y: number) => reach(x + 0.5, y + 0.5) <= flat + 0.06;
  const put = (ctx: CanvasRenderingContext2D, [r, g, b]: Rgb, x: number, y: number) => {
    ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
    ctx.fillRect(x, y, 1, 1);
  };
  const shade = (c: Rgb, x: number, y: number) => put(shadeTex.context, c, x, y);
  const light = (c: Rgb, x: number, y: number) => put(lightTex.context, c, x, y);
  for (let y = 0; y < H + HILL_SHADOW; y++) {
    for (let x = 0; x < W + HILL_SHADOW; x++) {
      if (!inside(x, y)) {
        if (inside(x - HILL_SHADOW, y - HILL_SHADOW) || inside(x - 1, y - HILL_SHADOW)) {
          shade(CAST, x, y);
        }
        continue;
      }
      const px = x + 0.5;
      const py = y + 0.5;
      const lean = (px - rx) / rx + (py - ry) / ry; // < 0 toward the sun
      const foot = !inside(x - 1, y) || !inside(x + 1, y) || !inside(x, y - 1) || !inside(x, y + 1);
      if (foot && lean > -0.5) {
        shade(FOOT, x, y);
        continue;
      }
      // The summit's rim: a bright lip where the top breaks into the slope, on the sunny side.
      if (
        onTop(x, y) &&
        lean < 0.5 &&
        (!onTop(x, y + 1) || !onTop(x + 1, y) || !onTop(x - 1, y) || !onTop(x, y - 1))
      ) {
        light(LIP, x, y);
        continue;
      }
      // How the slope faces the sun, against flat ground.
      const gx = height(px - 0.5, py) - height(px + 0.5, py);
      const gy = height(px, py - 0.5) - height(px, py + 0.5);
      const t = (gx * SUN[0] + gy * SUN[1] + SUN[2]) / Math.hypot(gx, gy, 1) - SUN[2];
      if (t > 0.22) light(BRIGHT, x, y);
      else if (t > 0.07) light(LIT, x, y);
      else if (t < -0.36) shade(DEEP, x, y);
      else if (t < -0.1) shade(SHADE, x, y);
    }
  }
  shadeTex.refresh();
  lightTex.refresh();
  return keys;
}
